// src/hooks/useNarrowViewport.ts

import { useEffect, useState } from 'react';

/**
 * Width below which the window cannot hold a split workspace — a phone, or a
 * window narrowed to one. Matches Tailwind's `md`, the point at which the shell
 * already switches the sidebar to a drawer.
 */
export const NARROW_MAX_WIDTH = 767;

const QUERY = `(max-width: ${NARROW_MAX_WIDTH}px)`;

/**
 * Whether the viewport is too narrow for side-by-side panes.
 *
 * A media query rather than a container query on purpose: this decides how the
 * whole workspace is laid out, so the measurement that matters is the window,
 * not any element inside it. Controls that only need to shrink use the
 * `.pane-wide-only` container query instead — a pane can be narrow in a wide
 * window, and those are different questions.
 */
export function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(QUERY).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return narrow;
}
